import getVideoInfo from 'helpers/getVideoInfo'
import getExtensionFromPath from 'helpers/getExtensionFromPath'
import {
  AudioPolicy,
  DocPolicy,
  ImagePolicy,
  VideoPolicy,
} from '@wellesley/openapi'
import { UploadFileProps } from '@wellesley/ui'
import getImageResolution from 'helpers/image/getImageResolution'
import bytesToMB from 'helpers/size/bytesToMB'
import { ExtensionsMap } from '@wellesley/settings/types'
import { mapExtensions } from '@wellesley/settings/slices/platformSettingsSlice'

type UploadTypes = Omit<ExtensionsMap, 'OTHER'>

export type HelperProps = {
  media: UploadFileProps
  docPolicy: Required<DocPolicy>
  imgPolicy: Required<ImagePolicy>
  videoPolicy: Required<VideoPolicy>
  audioPolicy: Required<AudioPolicy>
}

const checkUploadLimits = async ({
  media,
  docPolicy,
  imgPolicy,
  videoPolicy,
  audioPolicy,
}: HelperProps) => {
  if (!imgPolicy || !videoPolicy) {
    return 'Media policy is not defined'
  }

  const extensions: UploadTypes = {
    IMAGE: mapExtensions(imgPolicy.extensions),
    VIDEO: mapExtensions(videoPolicy.extensions),
    AUDIO: mapExtensions(audioPolicy.extensions),
    DOCUMENT: mapExtensions(docPolicy.extensions),
  }

  const fileExtension = getExtensionFromPath(media.file.name) || ''

  const mediaType = Object.keys(extensions).find((key) =>
    extensions[key as keyof UploadTypes]?.includes(fileExtension),
  ) as keyof ExtensionsMap

  if (!mediaType) {
    return `Extension ${fileExtension} is not supported`
  }

  const isImage = mediaType === 'IMAGE'
  const isVideo = mediaType === 'VIDEO'

  if (isImage) {
    const info = await getImageResolution(media.file)
    const isTooBig = media.file.size > imgPolicy.maxSize
    const isHighRes =
      info && Math.max(info.width, info.height) > imgPolicy.maxResolution
    const isMatrixReached =
      info && info.width * info.height > imgPolicy.maxMatrix

    switch (true) {
      case isTooBig:
        return `Image is too big. Max size is ${bytesToMB(imgPolicy.maxSize)}`
      case isHighRes:
        return `Image resolution is too high. Max resolution is ${imgPolicy.maxResolution} pixels`
      case isMatrixReached:
        return `The uploaded image exceeds the maximum size allowed. Please choose an image with smaller dimensions or reduce the resolution and try again. Max resolution is ${imgPolicy.maxMatrix} pixels`
    }
  }

  if (isVideo) {
    const info = await getVideoInfo({ src: media.objectUrl })
    const isTooLong = info && info.duration > videoPolicy.maxDuration
    const isHighRes =
      info && Math.max(info.width, info.height) > videoPolicy.maxResolution
    const isTooBig = media.file.size > videoPolicy.maxSize
    const isMatrixReached =
      info && info.width * info.height > videoPolicy.maxMatrix

    switch (true) {
      case isTooLong:
        return `Video is too long. Max duration is ${videoPolicy.maxDuration} seconds`
      case isTooBig:
        return `Video is too big. Max size is ${bytesToMB(videoPolicy.maxSize)}`
      case isHighRes:
        return `Please resize your video. Max allowed resolution ${videoPolicy.maxResolution} pixels`
      case isMatrixReached:
        return `The uploaded video exceeds the maximum size allowed. Please choose a video with smaller dimensions or reduce the resolution and try again. Max resolution is ${videoPolicy.maxMatrix} pixels`
    }
  }
}

export default checkUploadLimits
